import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from "react";

import { isPolygonPredicateType } from "constants/filter.constants";

import { useMaxBinDefaults } from "hooks/useMaxBinDefaults";

import { PerformanceDateRange } from "models";
import { isShapefileBoundaryProperty } from "models/groupBy";

import { DefaultBinSettings } from "components/user-settings/models";

import {
  ActiveFilterNode,
  GroupByFilterDependencies,
  UseActiveFilterControllerResult
} from "../types";
import { updateGroupBy } from "../util";
import { getStoredPerformanceDateRangeFromSession } from "../util/getStoredPerformanceDateRangeFromSession";
import { setStoredPerformanceDateRangeToSession } from "../util/setStoredPerformanceDateRangeToSession";

export interface UseActiveFilterControllerProps {
  deps: GroupByFilterDependencies;
  checkedLegendItemsRef: React.MutableRefObject<
    { property: string; value: unknown; isOthersRollup?: boolean }[]
  >;
  setIsForecastToggleOn: Dispatch<SetStateAction<boolean>>;
  setSelectedForecastFolder: Dispatch<SetStateAction<string>>;
  localPerformanceDateRangeRef: React.MutableRefObject<
    Record<string, PerformanceDateRange>
  >;
  updateField: (fieldGb: unknown, updateBinsOnly?: boolean) => void;
  defaultBinSettings: Maybe<DefaultBinSettings>;
}

// EVA-6412 SR-004b (NEW, T033) — owns active-filter display/count, node-remove/
// double-click handlers, clear-all, reset-polygon, lifted verbatim out of
// GroupByFilterActivity.tsx. Consumes deps — never entityKind directly (fixes P2).
export const useActiveFilterController = ({
  deps,
  checkedLegendItemsRef,
  setIsForecastToggleOn,
  setSelectedForecastFolder,
  localPerformanceDateRangeRef,
  updateField,
  defaultBinSettings
}: UseActiveFilterControllerProps): UseActiveFilterControllerResult => {
  const {
    columnsDictionary,
    groupBy,
    filterId,
    clearAllPropertiesFilter,
    dispatchResetPolygonFilter,
    dispatchActiveFilterCount
  } = deps;
  const maxBinDefaultsEnabled = useMaxBinDefaults() === true;

  // Restores the pre-refactor filterId-change effect (original
  // GroupByFilterActivity.tsx ~line 845-847): dispatch the active-filter count
  // whenever the applied filterId changes. Dropped when lifted into
  // useBinSettingsController — this is its proper home (active-filter domain).
  useEffect(() => {
    dispatchActiveFilterCount(filterId.id);
  }, [filterId.id, dispatchActiveFilterCount]);

  // Mirrors the original's columnsDictionaryRef (deleted under P5). Needed here
  // specifically because PredicateTree.tsx captures onMouseDoubleClick in a
  // useRef ONCE at mount and never re-syncs it (onMouseDoubleClickRef, no
  // update effect) — so onActiveFilterDoubleClick's identity must stay stable
  // across renders. Reading columnsDictionary directly (a value that changes
  // once columns finish loading, shortly after mount) would otherwise freeze
  // the double-click handler on the still-empty initial dictionary forever,
  // causing every double-click to hit "Unable to find field". Restoring the
  // ref preserves original behavior without reintroducing the general P5
  // mirror-ref pattern elsewhere.
  const columnsDictionaryRef = useRef(columnsDictionary);
  useEffect(() => {
    columnsDictionaryRef.current = columnsDictionary;
  }, [columnsDictionary]);

  const onActiveFilterNodeRemoved = useCallback(
    (node: ActiveFilterNode) => {
      const prop = node.property;
      const values = node.value;
      if (
        isPolygonPredicateType(node.type) ||
        (node.predicates && isPolygonPredicateType(node.predicates[0].type))
      ) {
        if (
          node.source === "selection" ||
          (node.predicates && node.predicates[0].source === "selection")
        ) {
          dispatchResetPolygonFilter(true);
          return;
        }
        // A polygon that is not a lasso is the map view. Removing that node on
        // its own is handled where the delete starts, in ActiveFilter, which
        // never routes it here; what does arrive here is a whole branch going
        // out, and clearing the filter is what that has always meant.
        clearAllPropertiesFilter();
        return;
      }
      if (node.rollup) {
        // The Others predicate names no value, so its checked item is found by the flag.
        checkedLegendItemsRef.current = checkedLegendItemsRef.current?.filter(
          (li) => li.property !== prop || !(li.isOthersRollup || li.value === node.rollup)
        );
        return;
      }
      if ((!values || values.length === 0) && values !== "") {
        return;
      }
      let value: unknown = values;
      if (Array.isArray(value)) {
        if (value.length > 1) {
          value = `${value[0]} - ${value[1]}`;
        } else if (value.length === 1) {
          value = value[0];
        }
      }
      value = (value as string | number).toString();

      if (value === "0" && (node.operator === ">" || node.operator === "<")) {
        value = `${node.operator} 0`;
      } else if (value === "") {
        value = "n/a";
      }

      checkedLegendItemsRef.current = checkedLegendItemsRef.current?.filter((li) => {
        return li.property !== prop || li.value !== value;
      });
    },
    [checkedLegendItemsRef, clearAllPropertiesFilter, dispatchResetPolygonFilter]
  );

  const onActiveFilterDoubleClick = useCallback(
    (node: ActiveFilterNode) => {
      if (!node.actualValue.property) return;
      // A shapefile filtering entry has no column behind it — the toggle in
      // the field picker builds its predicate directly — so there is nothing
      // to focus on and the lookup would only warn that the field is missing.
      if (isShapefileBoundaryProperty(node.actualValue.property)) return;
      const field = node.actualValue.property;
      const bin = node.actualValue.bin;
      const nodePerformanceDateRange = node.actualValue.performanceDateRange;
      if (node.actualValue.isForecastToggleOn) {
        setIsForecastToggleOn(node.actualValue.isForecastToggleOn);
      }
      if (node.actualValue.selectedForecastFolderName) {
        setSelectedForecastFolder(node.actualValue.selectedForecastFolderName);
      }

      if (nodePerformanceDateRange) {
        const freshDateRanges = getStoredPerformanceDateRangeFromSession();
        const mergedDateRanges = {
          ...freshDateRanges,
          [field]: nodePerformanceDateRange
        };
        localPerformanceDateRangeRef.current = mergedDateRanges;
        setStoredPerformanceDateRangeToSession(mergedDateRanges);
      }

      updateGroupBy(
        field,
        columnsDictionaryRef.current,
        updateField,
        bin,
        node.actualValue.selectedForecastFolderName ?? "",
        "",
        node.actualValue.partialContextTitle ?? "",
        node.actualValue.isForecastToggleOn ?? false,
        node.actualValue.pdenSource ?? "Public",
        groupBy.entityKind,
        defaultBinSettings,
        "",
        nodePerformanceDateRange ?? localPerformanceDateRangeRef.current[field],
        maxBinDefaultsEnabled
      );
    },
    [
      setIsForecastToggleOn,
      setSelectedForecastFolder,
      localPerformanceDateRangeRef,
      updateField,
      groupBy.entityKind,
      defaultBinSettings,
      maxBinDefaultsEnabled
    ]
  );

  return {
    onActiveFilterNodeRemoved,
    onActiveFilterDoubleClick,
    clearAllPropertiesFilter
  };
};
